import { ChevronDown } from 'lucide-react'
import { SelectHTMLAttributes, forwardRef } from 'react'

interface SelectOption {
  value: string
  label: string
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  options: SelectOption[]
  placeholder?: string
  error?: boolean
  fullWidth?: boolean
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ options, placeholder, error, fullWidth = false, ...props }, ref) => {
    return (
      <div className={`relative ${fullWidth ? 'w-full' : ''}`}>
        <select
          ref={ref}
          className={`
            appearance-none bg-white dark:bg-gray-700 border rounded-lg px-4 py-2.5 pr-10
            text-sm font-medium text-gray-700 dark:text-gray-200
            hover:border-gray-300 dark:hover:border-gray-500
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
            cursor-pointer transition-all
            disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-500 disabled:cursor-not-allowed
            ${fullWidth ? 'w-full' : ''}
            ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 dark:border-gray-600'}
          `}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        </div>
      </div>
    )
  }
)

Select.displayName = 'Select'

// Simple styled select for inline use (without wrapper)
export const selectClassName = `
  appearance-none bg-white border border-gray-200 rounded-lg px-4 py-2.5 pr-10
  text-sm font-medium text-gray-700
  hover:border-gray-300
  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
  cursor-pointer transition-all
`

export const selectErrorClassName = `
  appearance-none bg-white border border-red-500 rounded-lg px-4 py-2.5 pr-10
  text-sm font-medium text-gray-700
  hover:border-red-400
  focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent
  cursor-pointer transition-all
`

// Chevron icon component for manual use
export function SelectChevron() {
  return (
    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
      <ChevronDown className="w-4 h-4 text-gray-500" />
    </div>
  )
}

export default Select
